import { useMemo } from 'react';
import { useAccessRequestNodeFragment } from '@frameio/access-requests/gql/fragments/generated/AccessRequestNode';
import { getFormattedDate } from '@frameio/utils';
import { NOTIFICATION_TYPE_ACCESS_REQUEST_SUBMITTED } from '../../../constants';
import { useFormatTimestamp } from '../../../hooks';
import { useFormatAccessRequestFooter } from '../../../hooks/useFormatAccessRequestFooter';
import type { NotificationGroupInfo } from '../types';
import { useFooterFragment } from './gql/generated/Footer';

export function useFooterData({ id, notificationType }: NotificationGroupInfo) {
  const isAccessRequest =
    notificationType === NOTIFICATION_TYPE_ACCESS_REQUEST_SUBMITTED;
  const { complete: completeNotificationFooter, data: notificationFooterData } =
    useFooterFragment({
      from: {
        id,
        __typename: notificationType,
        skip: isAccessRequest,
      },
    });

  const { complete: completeAccessRequestNode, data: accessRequestNodeData } =
    useAccessRequestNodeFragment({
      from: {
        id,
        __typename: 'AccessRequest',
        skip: !isAccessRequest,
      },
    });

  const complete = completeNotificationFooter || completeAccessRequestNode;

  const formattedAccessRequestFooterData = useFormatAccessRequestFooter(
    accessRequestNodeData
  );

  let resourceName;
  let resourceType;
  let timestamp;

  if (isAccessRequest) {
    resourceName = formattedAccessRequestFooterData.projectName;
    resourceType = formattedAccessRequestFooterData.__typename;
    timestamp = formattedAccessRequestFooterData.timestamp;
  } else {
    const content =
      'content' in notificationFooterData
        ? notificationFooterData?.content?.layout?.footer
        : null;

    const notificationLayoutItems = content?.[0];
    const notificationLayoutItemsContent =
      notificationLayoutItems && 'content' in notificationLayoutItems
        ? notificationLayoutItems.content
        : null;

    const projectNameItem = notificationLayoutItemsContent?.find(
      (item) => item?.__typename === 'NotificationLayoutItemProjectName'
    );
    const shareNameItem = notificationLayoutItemsContent?.find(
      (item) => item?.__typename === 'NotificationLayoutItemShareName'
    );
    const resourceItem = projectNameItem ?? shareNameItem;
    const timestampItem = notificationLayoutItemsContent?.find(
      (item) => item?.__typename === 'NotificationLayoutItemTimestamp'
    );

    resourceType = resourceItem?.__typename;
    resourceName =
      resourceItem && 'content' in resourceItem ? resourceItem.content : null;

    const timestampContent =
      timestampItem && 'content' in timestampItem ? timestampItem.content : '';
    timestamp = timestampContent ?? '';
  }
  const date = useMemo(() => new Date(timestamp), [timestamp]);

  const { formattedDate } = getFormattedDate({ date });

  /**
   * If the event occured today, use the relative timestamp
   * If the event occured 1 or more days ago:
   *  - For notifications, which are grouped by a section date header,
   *    we use the formatted timestamp in the cell footer
   *  - Access requests are not grouped by a date header,
   *    so we display the day, month and year in the cell footer
   *
   */
  const { timestamp: formattedTimestamp } = useFormatTimestamp({
    date,
    includeTime: !isAccessRequest,
  });

  return useMemo(() => {
    if (!complete) return null;

    return {
      resourceName,
      resourceType,
      timestamp,
      formattedTimestamp,
      formattedDate,
    };
  }, [
    complete,
    resourceName,
    resourceType,
    timestamp,
    formattedTimestamp,
    formattedDate,
  ]);
}
